import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Boxes, GitBranch, LoaderCircle, Plus, Save, Trash2 } from 'lucide-react';
import { apiFetch } from '../../utils/apiClient';


const requestJson = async (path, { method = 'GET', body } = {}) => {
  const response = await apiFetch(path, {
    method,
    headers: { Accept: 'application/json', ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = payload?.detail;
    const message = (detail && typeof detail === 'object' ? detail.message : detail)
      || payload?.error
      || `Request failed (${response.status})`;
    throw new Error(message);
  }
  return payload;
};

const latestVersion = (module) => (
  (Array.isArray(module?.versions) ? module.versions : [])
    .slice()
    .sort((left, right) => Number(right.version || 0) - Number(left.version || 0))[0]
  || null
);

export const buildComposerNodes = (versions = []) => versions.map((version, index) => ({
  id: `node-${index + 1}`,
  module_version_id: String(version?.id || version),
  alias: '',
  input_bindings: {},
}));

export const moveComposerItem = (items, index, offset) => {
  const target = index + offset;
  if (target < 0 || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

export default function FlowModuleComposer({ session, selectedStepIds = [], onFlowCreated }) {
  const applicationId = String(session?.application_id || session?.application?.id || '');
  const [modules, setModules] = useState([]);
  const [sequence, setSequence] = useState([]);
  const [componentName, setComponentName] = useState('');
  const [flowName, setFlowName] = useState('');
  const [variants, setVariants] = useState([]);
  const [mode, setMode] = useState('valid_permutations');
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');
  const isComposedFlow = session?.composition_mode === 'modules';

  const refresh = useCallback(async () => {
    if (!applicationId) return;
    const payload = await requestJson(`/api/flow-modules?application_id=${encodeURIComponent(applicationId)}`);
    setModules(Array.isArray(payload?.modules) ? payload.modules : []);
  }, [applicationId]);

  useEffect(() => {
    refresh().catch((error) => setMessage(error.message));
  }, [refresh]);

  useEffect(() => {
    const nodes = Array.isArray(session?.flow_definition?.nodes) ? session.flow_definition.nodes : [];
    setSequence(nodes.map((node, index) => ({
      key: String(node.id || `node-${index + 1}`),
      nodeId: String(node.id || `node-${index + 1}`),
      versionId: String(node.module_version_id || ''),
      alias: String(node.alias || ''),
      inputBindings: node.input_bindings || {},
    })).filter((item) => item.versionId));
    const currentName = String(session?.feature_name || '');
    setFlowName(session?.composition_mode === 'modules' ? currentName : `${currentName} composed`.trim());
  }, [session?.id, session?.feature_name, session?.composition_mode, session?.flow_definition]);

  const versionsById = useMemo(() => {
    const rows = new Map();
    modules.forEach((module) => (module.versions || []).forEach((version) => rows.set(String(version.id), { ...version, module })));
    return rows;
  }, [modules]);

  const saveAsComponent = async () => {
    const name = componentName.trim();
    if (!name || !session?.id) return;
    setBusy('component');
    setMessage('');
    try {
      const module = await requestJson('/api/flow-modules', {
        method: 'POST',
        body: {
          application_id: applicationId,
          feature_area_id: session.feature_area_id || '',
          name,
          description: `Reusable component extracted from ${session.feature_name || 'captured flow'}`,
        },
      });
      await requestJson(`/api/flow-modules/${encodeURIComponent(module.id)}/versions/from-capture`, {
        method: 'POST',
        body: { capture_id: session.id, step_ids: selectedStepIds },
      });
      setComponentName('');
      await refresh();
      setMessage(`Saved ${name}. Version 1 is ready to reuse.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy('');
    }
  };

  const addModule = (module) => {
    const version = latestVersion(module);
    if (!version) return;
    setSequence((current) => [...current, {
      key: `${version.id}-${Date.now()}-${current.length}`,
      nodeId: `node-${Date.now()}-${current.length + 1}`,
      versionId: String(version.id),
      alias: '',
      inputBindings: {},
    }]);
  };

  const addComponentVersion = async (module) => {
    if (!session?.id) return;
    setBusy(`version-${module.id}`);
    setMessage('');
    try {
      const version = await requestJson(`/api/flow-modules/${encodeURIComponent(module.id)}/versions/from-capture`, {
        method: 'POST',
        body: { capture_id: session.id, step_ids: selectedStepIds, changelog: `Recaptured from ${session.feature_name || 'flow'}` },
      });
      await refresh();
      setMessage(`Published ${module.name} v${version.version}. Existing flows remain pinned until upgraded.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy('');
    }
  };

  const validateOrCompose = async (compose, updateCurrent = false) => {
    if (!sequence.length) return;
    setBusy(compose ? 'compose' : 'validate');
    setMessage('');
    try {
      const body = {
        feature_name: flowName.trim() || `${session?.feature_name || 'Feature'} composed flow`,
        application_id: applicationId,
        feature_area_id: session?.feature_area_id || '',
        notes: session?.notes || '',
        nodes: sequence.map((item, index) => ({
          id: item.nodeId || `node-${index + 1}`,
          module_version_id: item.versionId,
          alias: item.alias || '',
          input_bindings: item.inputBindings || {},
        })),
      };
      const target = compose && updateCurrent
        ? `/api/feature-flows/${encodeURIComponent(session.id)}/composition`
        : compose ? '/api/feature-flows/compose' : '/api/feature-flows/validate';
      const payload = await requestJson(target, {
        method: compose && updateCurrent ? 'PUT' : 'POST', body,
      });
      if (compose) {
        setMessage('Composed flow created. Its test cases and scripts can now be generated normally.');
        onFlowCreated?.(payload);
      } else {
        const warningCount = Array.isArray(payload?.warnings) ? payload.warnings.length : 0;
        setMessage(payload?.valid ? `Flow is valid${warningCount ? ` with ${warningCount} boundary warning(s)` : ''}.` : 'Flow is not valid.');
      }
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy('');
    }
  };

  const createVariant = async (variant) => {
    setBusy(`variant-${variant.id}`);
    try {
      const payload = await requestJson('/api/feature-flows/compose', {
        method: 'POST',
        body: {
          feature_name: `${flowName.trim() || session?.feature_name || 'Feature'} - ${variant.labels.join(' - ')}`.slice(0, 160),
          application_id: applicationId,
          feature_area_id: session?.feature_area_id || '',
          notes: session?.notes || '',
          nodes: buildComposerNodes(variant.module_version_ids),
        },
      });
      onFlowCreated?.(payload);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy('');
    }
  };

  const generateVariants = async () => {
    const ids = Array.from(new Set(sequence.map((item) => item.versionId)));
    if (!ids.length) return;
    setBusy('variants');
    try {
      const payload = await requestJson('/api/feature-flows/variants', {
        method: 'POST',
        body: { application_id: applicationId, module_version_ids: ids, mode, min_size: 1, max_size: ids.length },
      });
      setVariants(payload?.variants || []);
      setMessage(`Generated ${(payload?.variants || []).length} valid flow variant(s).`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-600 text-white"><Boxes className="h-5 w-5" /></span>
          <div>
            <h3 className="text-base font-bold text-slate-900">Reusable flow components</h3>
            <p className="mt-1 text-sm text-slate-600">Record a few small, linear flows with clicks and entered values. Select steps in the Steps tab to save a versioned component, or save the whole capture.</p>
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <input className="text-input flex-1" value={componentName} onChange={(event) => setComponentName(event.target.value)} placeholder="Component name, e.g. Add customer" />
          <button type="button" className="primary-btn px-4 py-2 text-sm" disabled={!componentName.trim() || busy} onClick={saveAsComponent}>
            {busy === 'component' ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save {selectedStepIds.length ? `${selectedStepIds.length} steps` : 'capture'}
          </button>
        </div>
      </section>

      <div className="grid gap-4 xl:grid-cols-[minmax(260px,0.8fr)_minmax(420px,1.2fr)]">
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900">Component library</h3>
          <div className="mt-3 space-y-2">
            {modules.map((module) => {
              const version = latestVersion(module);
              return (
                <div key={module.id} className="rounded-xl border border-slate-200 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span><span className="block text-sm font-semibold text-slate-900">{module.name}</span><span className="text-xs text-slate-500">{version ? `v${version.version} · ${version.step_count} steps` : 'No published version'}</span></span>
                    <button type="button" disabled={!version} onClick={() => addModule(module)} className="inline-flex items-center gap-1 rounded-lg bg-indigo-50 px-2 py-1 text-xs font-semibold text-indigo-700 disabled:opacity-50"><Plus className="h-3.5 w-3.5" /> Add</button>
                  </div>
                  {!isComposedFlow && (
                    <button type="button" disabled={Boolean(busy)} onClick={() => addComponentVersion(module)} className="mt-2 text-xs font-semibold text-slate-600 hover:text-indigo-700 disabled:opacity-40">
                      {busy === `version-${module.id}` ? 'Publishing…' : `Publish ${selectedStepIds.length ? 'selected steps' : 'capture'} as v${Number(module.latest_version || 0) + 1}`}
                    </button>
                  )}
                </div>
              );
            })}
            {!modules.length && <p className="rounded-xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">No components yet.</p>}
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2"><GitBranch className="h-4 w-4 text-indigo-600" /><h3 className="text-sm font-bold text-slate-900">Linear flow composer</h3></div>
          <p className="mt-1 text-xs text-slate-600">Arrange reusable recorded actions here. Describe any additional behavior in test notes when generating scripts.</p>
          <input className="text-input mt-3" value={flowName} onChange={(event) => setFlowName(event.target.value)} placeholder="New flow name" />
          <div className="mt-3 space-y-2">
            {sequence.map((item, index) => {
              const entry = versionsById.get(item.versionId);
              const newest = latestVersion(entry?.module);
              return (
                <div key={item.key} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">{index + 1}</span>
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold">{entry?.module?.name || item.versionId} <span className="text-xs font-normal text-slate-500">v{entry?.version || '?'}</span></span>
                  {newest && String(newest.id) !== item.versionId && (
                    <button type="button" className="rounded-lg bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-700" onClick={() => setSequence((rows) => rows.map((row) => row.key === item.key ? { ...row, versionId: String(newest.id) } : row))}>Use v{newest.version}</button>
                  )}
                  <button type="button" aria-label="Move component up" onClick={() => setSequence((rows) => moveComposerItem(rows, index, -1))}><ArrowUp className="h-4 w-4" /></button>
                  <button type="button" aria-label="Move component down" onClick={() => setSequence((rows) => moveComposerItem(rows, index, 1))}><ArrowDown className="h-4 w-4" /></button>
                  <button type="button" aria-label="Remove component" onClick={() => setSequence((rows) => rows.filter((row) => row.key !== item.key))}><Trash2 className="h-4 w-4 text-rose-600" /></button>
                </div>
              );
            })}
            {!sequence.length && <p className="rounded-xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">Add components from the library in the order they should run.</p>}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" className="secondary-btn px-3 py-2 text-sm" disabled={!sequence.length || busy} onClick={() => validateOrCompose(false)}>Validate</button>
            <button type="button" className="primary-btn px-3 py-2 text-sm" disabled={!sequence.length || busy} onClick={() => validateOrCompose(true)}>{busy === 'compose' && <LoaderCircle className="h-4 w-4 animate-spin" />}Create flow</button>
            {isComposedFlow && (
              <button type="button" className="secondary-btn px-3 py-2 text-sm" disabled={!sequence.length || busy} onClick={() => validateOrCompose(true, true)}>Update this flow</button>
            )}
            <select className="rounded-lg border border-slate-200 px-2 text-sm" value={mode} onChange={(event) => setMode(event.target.value)}>
              <option value="valid_permutations">Valid permutations</option>
              <option value="combinations">Combinations</option>
            </select>
            <button type="button" className="secondary-btn px-3 py-2 text-sm" disabled={!sequence.length || busy} onClick={generateVariants}>Generate variants</button>
          </div>
        </section>
      </div>

      {message && <p className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700" role="status">{message}</p>}
      {variants.length > 0 && (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900">Valid variants</h3>
          <div className="mt-3 grid gap-2 md:grid-cols-2">
            {variants.map((variant) => (
              <div key={variant.id} className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm">
                <span>{variant.labels.join(' → ')}</span>
                <button type="button" className="rounded-lg bg-indigo-50 px-2 py-1 text-xs font-semibold text-indigo-700" disabled={Boolean(busy)} onClick={() => createVariant(variant)}>
                  {busy === `variant-${variant.id}` ? 'Creating…' : 'Create flow'}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
